import { createContext } from 'react';
import type { ComponentType, ReactNode } from 'react';
import type { LinkProps } from '@tanstack/react-router';

/**
 * 🔴 THE KIT DRAWS A ROUTE LINK WITHOUT KNOWING THE ROUTER — DS-9b.
 *
 * `BackLink` imported TanStack's `Link` at runtime, so every app that drew a `PageHeader` shipped the router — the
 * operator console too, which routes by hash and never used it (its first load went 266 -> 274.1 KB raw when Companies,
 * Health and Licence took the kit's head). The kit now asks this context for the app's link instead: the back office
 * (routes.tsx, the root route), the portal (PortalApp.tsx) and the spec harness provide TanStack's `Link`; the console
 * provides nothing, and has no back links.
 *
 * ⚠️ `import type` only, here and anywhere under components/: ui-gates.spec.ts refuses a runtime import of the router
 * there, so the kit stays router-free.
 */
export type RouteLinkProps = {
  to: LinkProps['to'];
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  className?: string;
  children: ReactNode;
};

/** The app's link component: TanStack's `Link`, in an app that routes with it. */
export type RouteLink = ComponentType<RouteLinkProps>;

/**
 * ⚠️ Read with React's `use` where it is drawn (BackLink, PageHeader.tsx), not through a hook here: this module is in
 * every app's first load, and every byte in it is paid on first paint — the back office's and the console's ratchets
 * sit within a few bytes of it.
 */
export const RouteLinkContext = createContext<RouteLink | null>(null);

/**
 * Hands the kit the app's link component for the tree below: the spec harness uses it. The two shells render
 * `RouteLinkContext.Provider` themselves, which keeps this function out of their first load.
 */
export function RouteLinkProvider({ link, children }: { link: RouteLink; children: ReactNode }) {
  return <RouteLinkContext.Provider value={link}>{children}</RouteLinkContext.Provider>;
}
